'use client'

import { cx } from 'class-variance-authority'
import { ReviewStatus } from '@skop-app/lib-types'
import { useTranslations } from 'next-intl'

type ReviewStatusBadgeProps = {
  status?: ReviewStatus
  size?: 'default' | 'compact'
}

const getStatusPresentation = (
  status: ReviewStatus | undefined,
  t: ReturnType<typeof useTranslations>,
) => {
  switch (status) {
    case ReviewStatus.PENDING:
      return {
        label: t('Review.pending'),
        className:
          'bg-amber-50/80 text-amber-700 ring-1 ring-inset ring-amber-200/80',
      }
    case ReviewStatus.ACCEPTED:
      return {
        label: t('Review.accepted'),
        className:
          'bg-lime-50/80 text-lime-700 ring-1 ring-inset ring-lime-200/80',
      }
    case ReviewStatus.REJECTED:
      return {
        label: t('Review.rejected'),
        className:
          'bg-rose-50/80 text-rose-700 ring-1 ring-inset ring-rose-200/80',
      }
    default:
      return null
  }
}

export function ReviewStatusBadge({
  status,
  size = 'default',
}: ReviewStatusBadgeProps) {
  const t = useTranslations()
  const statusPresentation = getStatusPresentation(status, t)

  if (!statusPresentation) return null

  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full font-medium',
        size === 'compact'
          ? 'px-1.5 py-0.5 text-[10px]'
          : 'px-2 py-0.5 text-xs',
        statusPresentation.className,
      )}
    >
      {statusPresentation.label}
    </span>
  )
}

ReviewStatusBadge.displayName = 'ReviewStatusBadge'
